/**
 * InstructionsRenderer — parses task instruction templates into structured
 * cards. Templates authored via `/app/backend/services/instruction_templates.py`
 * use ALL-CAPS section headings (RULES, VIOLATIONS, AUTHENTICITY) plus a
 * trailing "By continuing..." consent line — we detect all three and render
 * each with distinct affordances so the intro screen reads like an onboarding,
 * not a wall of text.
 *
 * Falls back to a single clean card when instructions have no headings
 * (custom admin edits with prose only).
 */
import { CheckSquare, AlertOctagon, Sparkles, Info } from "lucide-react";

/** Detect an ALL-CAPS heading line (>=2 letters, all uppercase). */
function isHeadingLine(ln) {
  const s = (ln || "").trim();
  if (!s || s.length < 3) return false;
  const letters = s.replace(/[^A-Za-z]/g, "");
  if (letters.length < 2) return false;
  return letters === letters.toUpperCase();
}

/**
 * parseInstructions(text) → { intro, sections, consent }.
 * • intro    = paragraph(s) before the first ALL-CAPS heading
 * • sections = ordered list of { heading, body }
 * • consent  = trailing "By continuing..." paragraph (lifted out of the last section)
 */
export function parseInstructions(text) {
  const out = { intro: "", sections: [], consent: "" };
  if (!text || !text.trim()) return out;

  // Pull the "By continuing..." line off the end (case-insensitive).
  let working = text.trim();
  const consentRegex = /(?:^|\n\s*\n)(By continuing[^]*?)$/i;
  const m = working.match(consentRegex);
  if (m) {
    out.consent = m[1].trim();
    working = working.slice(0, m.index).trim();
  }

  const lines = working.split("\n");
  let currentHeading = null;
  let bucket = [];
  const flush = () => {
    const body = bucket.join("\n").trim();
    if (currentHeading === null) {
      out.intro = body;
    } else if (body) {
      out.sections.push({ heading: currentHeading, body });
    }
    bucket = [];
  };
  for (const ln of lines) {
    if (isHeadingLine(ln)) {
      flush();
      currentHeading = ln.trim();
    } else {
      bucket.push(ln);
    }
  }
  flush();
  return out;
}

/** Map a heading string to a visual variant. */
function variantForHeading(h) {
  const s = (h || "").toLowerCase();
  if (s.includes("violat")) return "violations";
  if (s.includes("rule"))   return "rules";
  if (s.includes("authent") || s.includes("honest")) return "authenticity";
  return "generic";
}

/** Split a section body into paragraph/list blocks. */
function parseBlocks(body) {
  const raw = (body || "").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return raw.map((block) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length && lines.every((l) => /^\d+\.\s/.test(l))) {
      return { kind: "ol", items: lines.map((l) => l.replace(/^\d+\.\s+/, "")) };
    }
    if (lines.length && lines.every((l) => /^[•\-]\s/.test(l))) {
      return { kind: "ul", items: lines.map((l) => l.replace(/^[•\-]\s+/, "")) };
    }
    return { kind: "p", text: block };
  });
}

function Blocks({ blocks, variant }) {
  const bulletColor =
    variant === "violations" ? "text-rose-500" :
    variant === "rules"      ? "text-emerald-600" :
    variant === "authenticity" ? "text-amber-600" :
    "text-neutral-400";
  return (
    <div className="space-y-2 text-sm leading-relaxed text-neutral-800">
      {blocks.map((b, i) => {
        if (b.kind === "ol") {
          return (
            <ol key={i} className="ml-5 list-decimal space-y-1 marker:font-semibold marker:text-neutral-500">
              {b.items.map((it, j) => <li key={j} className="pl-1">{it}</li>)}
            </ol>
          );
        }
        if (b.kind === "ul") {
          return (
            <ul key={i} className="space-y-1">
              {b.items.map((it, j) => (
                <li key={j} className="flex gap-2">
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current ${bulletColor}`} aria-hidden="true" />
                  <span className="min-w-0 flex-1">{it}</span>
                </li>
              ))}
            </ul>
          );
        }
        return <p key={i} className="whitespace-pre-wrap">{b.text}</p>;
      })}
    </div>
  );
}

const CARD_STYLES = {
  rules: {
    wrap: "rounded-lg border-l-4 border-emerald-500 bg-emerald-50/40 p-4",
    icon: CheckSquare,
    iconClass: "text-emerald-700",
    heading: "text-emerald-800",
  },
  violations: {
    wrap: "rounded-lg border-l-4 border-rose-500 bg-rose-50/40 p-4",
    icon: AlertOctagon,
    iconClass: "text-rose-700",
    heading: "text-rose-800",
  },
  authenticity: {
    wrap: "rounded-lg border-l-4 border-amber-600 bg-amber-50/40 p-4",
    icon: Sparkles,
    iconClass: "text-amber-700",
    heading: "text-amber-800",
  },
  generic: {
    wrap: "rounded-lg border-l-4 border-neutral-300 bg-neutral-50 p-4",
    icon: Info,
    iconClass: "text-neutral-600",
    heading: "text-neutral-800",
  },
};

function SectionCard({ heading, body }) {
  const variant = variantForHeading(heading);
  const s = CARD_STYLES[variant];
  const Icon = s.icon;
  const blocks = parseBlocks(body);
  return (
    <div className={s.wrap} data-testid={`instructions-section-${variant}`}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className={`h-5 w-5 ${s.iconClass}`} aria-hidden="true" />
        <h3 className={`text-sm font-semibold uppercase tracking-wide ${s.heading}`}>
          {heading}
        </h3>
      </div>
      <Blocks blocks={blocks} variant={variant} />
    </div>
  );
}

/**
 * Renders parsed instructions (intro + section cards).
 * Consent line is intentionally NOT rendered here — the parent (IntroScreen)
 * places it right above the consent checkbox.
 */
export default function InstructionsRenderer({ parsed }) {
  if (!parsed || (!parsed.intro && !parsed.sections?.length)) return null;

  // Fallback: no ALL-CAPS structure found → render intro as a single clean card.
  if (parsed.intro && parsed.sections.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4"
        data-testid="instructions-fallback">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
          {parsed.intro}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="instructions-structured">
      {parsed.intro && (
        <p className="whitespace-pre-wrap text-base leading-relaxed text-neutral-700"
          data-testid="instructions-intro">
          {parsed.intro}
        </p>
      )}
      {parsed.sections.map((sec, i) => (
        <SectionCard key={i} heading={sec.heading} body={sec.body} />
      ))}
    </div>
  );
}
